<script lang="ts" setup>
import { computed, ref } from 'vue';
import { DropZone } from './index';
import { ErrorMessage } from '../../ErrorMessage';
import { HelpText } from '../../HelpText';
import InputLabel from '../../InputLabel/components/InputLabel.vue';
import { useI18n } from 'vue-i18n';
import { Button, TextStyle } from '@returnless/focus-ui';

const { t } = useI18n();

const {
  allowedFileTypes = [],
  error = null,
  fileSizeLimit = null,
  videoSizeLimit = null,
  label = null,
  multiple = false,
  onFilesUpload,
} = defineProps<{
  allowedFileTypes?: string[];
  error?: string | null;
  fileSizeLimit?: number | null;
  videoSizeLimit?: number | null;
  label?: string | null;
  multiple?: boolean;
  onFilesUpload: (files: File[]) => void;
}>();

const internalError = ref<string | null>(null);

const errorMessage = computed<string | null>(() => error || internalError.value);

const accept = computed<string>(() => {
  return allowedFileTypes.join(',');
});

const nonVideoFileTypes = computed<string[]>(() =>
  allowedFileTypes.filter((type) => type.startsWith('video/')),
);
const videoFileTypes = computed<string[]>(() =>
  allowedFileTypes.filter((type) => type.startsWith('video/')),
);

const formattedNonVideoFileTypes = computed<string>(() =>
  nonVideoFileTypes.value.map((type) => type.split('/').pop()?.toUpperCase() ?? '').join(', '),
);
const formattedVideoFileTypes = computed<string>(() =>
  videoFileTypes.value.map((type) => type.split('/').pop()?.toUpperCase() ?? '').join(', '),
);

const formattedFileSizeLimit = computed<number | null>(() =>
  fileSizeLimit ? (fileSizeLimit / 1024 / 1024) : null,
);

const formattedVideoSizeLimit = computed<number | null>(() =>
  videoSizeLimit ? (videoSizeLimit / 1024 / 1024) : null,
);

function onInputChange(e: InputEvent): void {
  const target = e.target as HTMLInputElement;

  onFilesUpload([...target.files]);

  target.files = null;
  target.value = '';
}

const fileInput = ref(null);

function openFileUploadBrowser(): void {
  internalError.value = null;
  fileInput.value.click();
}
</script>

<template>
  <div>
    <InputLabel
      v-if="label"
      :label="label"
      :flush="!! $slots['help-text']"
    />

    <HelpText v-if="!! $slots['help-text']">
      <slot name="help-text" />
    </HelpText>

    <DropZone
      v-slot="{ dropZoneActive }"
      @files-dropped="onFilesUpload"
    >
      <div
        class="mt-1 flex cursor-pointer flex-col space-y-4 rounded-md border border-dashed px-6 pb-6 pt-5"
        :class="{
          'border-slate-400 bg-slate-50': dropZoneActive,
          'border-red-300': errorMessage,
          'border-slate-300': !errorMessage,
        }"
        @click="openFileUploadBrowser"
      >
        <div class="flex flex-col items-center space-y-3 text-center">
          <svg
            class="size-10"
            viewBox="0 0 40 40"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M32 13V36C32 37.105 31.105 38 30 38H6C4.895 38 4 37.105 4 36V4C4 2.895 4.895 2 6 2H21L24 10L32 13Z"
              class="fill-slate-100"
            />
            <path
              d="M21 2V11C21 12.105 21.895 13 23 13H32L21 2Z"
              class="fill-slate-200"
            />
            <circle
              cx="29"
              cy="30"
              r="9"
              class="fill-brand-500"
            />
            <path
              d="M32.75 31.25V32.9167C32.75 33.1377 32.6622 33.3496 32.5059 33.5059C32.3496 33.6622 32.1377 33.75 31.9167 33.75H26.0833C25.8623 33.75 25.6504 33.6622 25.4941 33.5059C25.3378 33.3496 25.25 33.1377 25.25 32.9167V31.25M31.0833 28.0833L29 26M29 26L26.9167 28.0833M29 26V31"
              stroke="white"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>

          <TextStyle>
            <input
              ref="fileInput"
              class="hidden"
              type="file"
              :accept="accept"
              :multiple="multiple"
              @change="onInputChange"
            >

            <div class="flex space-x-1">
              <span>
                {{ t('customer.global:file-upload:choose-file') }}
                {{ t('customer.global:file-upload:drag-and-drop') }}
              </span>
            </div>
          </TextStyle>
          <div
            v-if="nonVideoFileTypes.length && formattedFileSizeLimit"
            class="flex justify-center"
          >
            <span class="text-balance text-center text-xs text-slate-400">
              {{ formattedNonVideoFileTypes }} {{ t('customer.global:file-upload:up-to') }} {{ formattedFileSizeLimit }}MB
            </span>
          </div>
          <div
            v-if="videoFileTypes.length && formattedVideoSizeLimit"
            class="flex justify-center"
          >
            <span class="text-balance text-center text-xs text-slate-400">
              {{ formattedVideoFileTypes }} {{ t('customer.global:file-upload:up-to') }} {{ formattedVideoSizeLimit }}MB
            </span>
          </div>

          <Button variant="outline">
            {{ t('customer.global:file-upload:browse-files') }}
          </Button>
        </div>
      </div>

      <ErrorMessage
        v-if="errorMessage"
        :message="errorMessage"
      />
    </DropZone>
  </div>
</template>
